import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, Inbox, MapPin, Briefcase, Phone, Plane, Trash2, Edit3, Plus, X, Save, Loader2, Mail, Calendar, Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  isAuthed, verifyToken, clearToken,
  adminListEnquiries, adminDeleteEnquiry,
  adminListContent, adminUpsertContent, adminDeleteContent,
  adminListAgentRegistrations, adminDeleteAgentRegistration,
} from "@/lib/admin-api";
import {
  DESTINATIONS as DEFAULT_DESTINATIONS,
  SERVICES as DEFAULT_SERVICES,
  DEPARTMENTS as DEFAULT_DEPARTMENTS,
} from "@/lib/data";

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "agent_registrations", label: "Agent Registrations", icon: Users },
  { id: "destinations", label: "Destinations", icon: MapPin },
  { id: "services", label: "Services", icon: Briefcase },
  { id: "departments", label: "Departments", icon: Phone },
  { id: "airline_partners", label: "Airline Partners", icon: Plane },
];

const DEFAULTS = {
  destinations: DEFAULT_DESTINATIONS,
  services: DEFAULT_SERVICES,
  departments: DEFAULT_DEPARTMENTS,
  airline_partners: [],
};

const SLUG_KEY = {
  destinations: "slug",
  services: "slug",
  departments: "id",
  airline_partners: "slug",
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (!isAuthed()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    verifyToken()
      .then(() => setReady(true))
      .catch(() => {
        clearToken();
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  if (!ready) {
    return (
      <div className="min-h-[60vh] grid place-items-center -mt-24 pt-24">
        <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div className="-mt-24 pt-24 min-h-screen bg-linen" data-testid="admin-dashboard">
      <div className="border-b border-ink/10 bg-linen sticky top-[68px] z-30">
        <div className="container-edge py-5 flex items-center justify-between gap-4">
          <div>
            <div className="overline text-terracotta">Simba Travel CMS</div>
            <h1 className="font-serif text-2xl">Content management</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-secondary text-xs">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <div className="container-edge flex overflow-x-auto gap-1 pb-3 border-b border-ink/5">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-xs tracking-widest uppercase transition-colors whitespace-nowrap ${
                tab === t.id ? "bg-midnight text-linen" : "text-ink/70 hover:text-terracotta"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="container-edge py-12">
        {tab === "enquiries" && <EnquiriesPanel />}
        {tab === "agent_registrations" && <AgentRegistrationsPanel />}
        {tab !== "enquiries" && tab !== "agent_registrations" && <ContentPanel collection={tab} />}
      </div>
    </div>
  );
}

// ---------- Enquiries ----------
function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListEnquiries(filter);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await adminDeleteEnquiry(id);
      setItems((x) => x.filter((e) => e.id !== id));
      setSelected(null);
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-enquiries-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline">Inbox</div>
          <h2 className="editorial-h2">{items.length} enquiries</h2>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="admin-enquiry-filter"
          className="input-line max-w-xs"
        >
          <option value="">All types</option>
          {["airline-partnership", "travel-agent", "corporate", "refund", "general", "destination", "group", "agency-registration"].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted2">No enquiries found.</div>
      ) : (
        <div className="border border-ink/10 divide-y divide-ink/10 bg-linen">
          {items.map((e) => (
            <div
              key={e.id}
              data-testid={`admin-enquiry-${e.id}`}
              className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start hover:bg-sand transition-colors"
            >
              <div className="md:col-span-2">
                <div className="text-[10px] tracking-widest uppercase text-terracotta">{e.enquiry_type}</div>
                <div className="text-xs text-muted2 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(e.created_at).toLocaleString("en-GB")}</div>
              </div>
              <div className="md:col-span-4">
                <div className="font-medium text-sm">{e.full_name}</div>
                <div className="text-xs text-muted2 flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {e.email}</div>
                {e.phone && <div className="text-xs text-muted2 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {e.phone}</div>}
              </div>
              <div className="md:col-span-4 text-sm text-ink/80 line-clamp-2">{e.subject ? <strong className="font-medium">{e.subject}: </strong> : null}{e.message}</div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <button onClick={() => setSelected(e)} className="text-xs px-3 py-1.5 border border-ink/20 hover:border-terracotta hover:text-terracotta">
                  View
                </button>
                <button onClick={() => remove(e.id)} className="text-xs px-3 py-1.5 border border-ink/20 hover:border-red-700 hover:text-red-700">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <Modal onClose={() => setSelected(null)} title="Enquiry detail">
          <div className="space-y-4 text-sm">
            <Row label="Type">{selected.enquiry_type}</Row>
            <Row label="Name">{selected.full_name}</Row>
            <Row label="Email"><a href={`mailto:${selected.email}`} className="text-terracotta">{selected.email}</a></Row>
            {selected.phone && <Row label="Phone"><a href={`tel:${selected.phone}`}>{selected.phone}</a></Row>}
            {selected.company && <Row label="Company">{selected.company}</Row>}
            {selected.subject && <Row label="Subject">{selected.subject}</Row>}
            {selected.destination && <Row label="Destination">{selected.destination}</Row>}
            {selected.iata_number && <Row label="IATA">{selected.iata_number}</Row>}
            <Row label="Message"><div className="whitespace-pre-wrap">{selected.message}</div></Row>
            <Row label="Received">{new Date(selected.created_at).toLocaleString("en-GB")}</Row>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- Agent Registrations ----------
function AgentRegistrationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListAgentRegistrations();
      setItems(data);
    } catch (e) {
      toast.error("Failed to load agent registrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this registration? This cannot be undone.")) return;
    try {
      await adminDeleteAgentRegistration(id);
      setItems((x) => x.filter((e) => e.id !== id));
      setSelected(null);
      toast.success("Registration deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-agent-registrations-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline">Trade agents</div>
          <h2 className="editorial-h2">{items.length} registrations</h2>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted2">No registrations yet.</div>
      ) : (
        <div className="border border-ink/10 divide-y divide-ink/10 bg-linen">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-agent-reg-${r.id}`}
              className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start hover:bg-sand transition-colors"
            >
              <div className="md:col-span-2">
                <div className="text-[10px] tracking-widest uppercase text-terracotta font-mono">{r.reference}</div>
                <div className="text-xs text-muted2 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(r.created_at).toLocaleString("en-GB")}</div>
              </div>
              <div className="md:col-span-4">
                <div className="font-medium text-sm">{r.company_name}</div>
                <div className="text-xs text-muted2 mt-1">{[r.title, r.first_name, r.last_name].filter(Boolean).join(" ")}</div>
                <div className="text-xs text-muted2 flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {r.email}</div>
                {r.phone_number && <div className="text-xs text-muted2 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {r.phone_number}</div>}
              </div>
              <div className="md:col-span-4 text-xs text-ink/80">
                <div>{[r.address_1, r.city, r.postal_code, r.country].filter(Boolean).join(", ")}</div>
                {r.iata_number && <div className="mt-1"><span className="text-muted2">IATA:</span> {r.iata_number}</div>}
                {r.atol_number && <div className="mt-1"><span className="text-muted2">ATOL:</span> {r.atol_number}</div>}
                {r.abta_number && <div className="mt-1"><span className="text-muted2">ABTA:</span> {r.abta_number}</div>}
              </div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <button onClick={() => setSelected(r)} className="text-xs px-3 py-1.5 border border-ink/20 hover:border-terracotta hover:text-terracotta" data-testid={`admin-agent-reg-view-${r.id}`}>
                  View
                </button>
                <button onClick={() => remove(r.id)} className="text-xs px-3 py-1.5 border border-ink/20 hover:border-red-700 hover:text-red-700" data-testid={`admin-agent-reg-delete-${r.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <Modal onClose={() => setSelected(null)} title={`Registration · ${selected.reference}`}>
          <div className="space-y-3 text-sm">
            <Row label="Company">{selected.company_name}</Row>
            {selected.website && <Row label="Website">{selected.website}</Row>}
            <Row label="Contact">{[selected.title, selected.first_name, selected.last_name].filter(Boolean).join(" ")}</Row>
            <Row label="Email"><a href={`mailto:${selected.email}`} className="text-terracotta">{selected.email}</a></Row>
            <Row label="Phone"><a href={`tel:${selected.phone_number}`}>{selected.phone_number}</a></Row>
            {selected.mobile_number && <Row label="Mobile">{selected.mobile_number}</Row>}
            {selected.fax_number && <Row label="Fax">{selected.fax_number}</Row>}
            <Row label="Address">
              {[selected.address_1, selected.address_2, selected.city, selected.postal_code, selected.county_state, selected.country].filter(Boolean).join(", ")}
            </Row>
            {selected.iata_number && <Row label="IATA">{selected.iata_number}</Row>}
            {selected.atol_number && <Row label="ATOL">{selected.atol_number}</Row>}
            {selected.abta_number && <Row label="ABTA">{selected.abta_number}</Row>}
            {selected.consortium && <Row label="Consortium">{selected.consortium}</Row>}
            {selected.backup_contact_name && <Row label="Backup contact">{selected.backup_contact_name} — {selected.backup_contact_number}</Row>}
            {selected.remarks && <Row label="Remarks"><div className="whitespace-pre-wrap">{selected.remarks}</div></Row>}
            <Row label="Received">{new Date(selected.created_at).toLocaleString("en-GB")}</Row>
            {selected.ip && <Row label="Source IP"><span className="font-mono text-xs">{selected.ip}</span></Row>}
          </div>
        </Modal>
      )}
    </div>
  );
}


function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-b border-ink/5 pb-3">
      <div className="text-[11px] tracking-widest uppercase text-muted2">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

// ---------- Content ----------
function ContentPanel({ collection }) {
  const slugKey = SLUG_KEY[collection];
  const defaults = DEFAULTS[collection] || [];
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListContent(collection);
      setOverrides(data);
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  }, [collection]);

  useEffect(() => { load(); }, [load]);

  // Merge: defaults + any DB items not in defaults
  const merged = [
    ...defaults.map((d) => {
      const override = overrides.find((o) => o.slug === (d[slugKey] || d.slug));
      return override ? { ...d, ...override, _hasOverride: true } : { ...d, _hasOverride: false };
    }),
    ...overrides.filter((o) => !defaults.find((d) => (d[slugKey] || d.slug) === o.slug)).map((o) => ({ ...o, _custom: true })),
  ];

  const save = async (slug, data) => {
    try {
      await adminUpsertContent(collection, slug, data);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed.");
    }
  };

  const remove = async (slug) => {
    if (!confirm(`Delete override for "${slug}"? Default content will be restored.`)) return;
    try {
      await adminDeleteContent(collection, slug);
      toast.success("Override removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const createNew = () => {
    setEditing({ slug: "", isNew: true, data: { name: "" } });
  };

  return (
    <div data-testid={`admin-content-${collection}`}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline">{collection.replace(/_/g, " ")}</div>
          <h2 className="editorial-h2 capitalize">{collection.replace(/_/g, " ")}</h2>
          <p className="text-sm text-muted2 mt-2">
            Edit existing entries to override defaults, or add new ones (e.g. additional airline partners). Removing an override restores the default content.
          </p>
        </div>
        <button onClick={createNew} className="btn-primary text-xs" data-testid="admin-content-new">
          <Plus className="w-4 h-4" /> Add new
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-terracotta" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {merged.map((item) => {
            const slug = item[slugKey] || item.slug;
            return (
              <div key={slug} className="bg-linen p-6" data-testid={`admin-item-${slug}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-muted2">{slug}</div>
                    <div className="font-serif text-xl mt-1">{item.name || slug}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {item._custom && <span className="text-[9px] uppercase tracking-widest bg-terracotta text-white px-2 py-0.5">Custom</span>}
                    {item._hasOverride && <span className="text-[9px] uppercase tracking-widest bg-ochre text-midnight px-2 py-0.5">Edited</span>}
                  </div>
                </div>
                {item.country && <div className="text-xs text-muted2">{item.country}</div>}
                {item.short && <div className="text-xs text-muted2 line-clamp-2 mt-2">{item.short}</div>}
                {item.phone && <div className="text-xs text-muted2 mt-2">{item.phone}</div>}
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => setEditing({ slug, data: { ...item }, isNew: false })}
                    data-testid={`admin-edit-${slug}`}
                    className="text-xs px-3 py-1.5 border border-ink/20 hover:border-terracotta hover:text-terracotta flex items-center gap-1"
                  >
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  {(item._hasOverride || item._custom) && (
                    <button
                      onClick={() => remove(slug)}
                      data-testid={`admin-delete-${slug}`}
                      className="text-xs px-3 py-1.5 border border-ink/20 hover:border-red-700 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditModal
          collection={collection}
          slug={editing.slug}
          isNew={editing.isNew}
          initial={editing.data}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditModal({ collection, slug, isNew, initial, onClose, onSave }) {
  const stripped = { ...initial };
  delete stripped._hasOverride;
  delete stripped._custom;
  delete stripped.updated_at;
  delete stripped.created_at;
  const [json, setJson] = useState(JSON.stringify(stripped, null, 2));
  const [slugVal, setSlugVal] = useState(slug || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      toast.error("Invalid JSON. Please fix syntax.");
      return;
    }
    if (!slugVal) {
      toast.error("Slug is required.");
      return;
    }
    setBusy(true);
    await onSave(slugVal, parsed);
    setBusy(false);
  };

  return (
    <Modal onClose={onClose} title={isNew ? `New ${collection.replace(/_/g, " ")}` : `Edit ${slug}`}>
      {isNew && (
        <div className="mb-4">
          <label className="label-line">Slug / ID</label>
          <input
            value={slugVal}
            onChange={(e) => setSlugVal(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
            data-testid="admin-edit-slug"
            className="input-line"
            placeholder="e.g. air-tanzania"
          />
        </div>
      )}
      <div>
        <label className="label-line">Content (JSON)</label>
        <p className="text-xs text-muted2 mb-2">Edit fields directly. Image URLs, descriptions, attractions arrays, etc.</p>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          data-testid="admin-edit-json"
          rows={20}
          className="w-full border border-ink/15 bg-sand p-4 text-xs font-mono text-ink focus:outline-none focus:border-terracotta resize-y"
        />
      </div>
      <div className="flex gap-3 mt-6 justify-end">
        <button onClick={onClose} className="btn-secondary text-xs">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="admin-edit-save" className="btn-primary text-xs disabled:opacity-60">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Save className="w-4 h-4" /> Save</>}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, title }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-midnight/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="admin-modal">
      <div className="bg-linen max-w-2xl w-full max-h-[88vh] overflow-y-auto p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="editorial-h3">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-sand" data-testid="admin-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
